'use client'

import { Link } from '@repo/web-ui/components/link'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { useLocale } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { tw } from 'utils/classnames'

interface PaginationProps {
  page: number
  maxPage: number
  route: string
  pathname: string
}

export function Pagination({ page, maxPage, route, pathname }: PaginationProps) {
  const searchParams = useSearchParams()
  const orderby = searchParams.get('orderby')
  const query = orderby ? `?orderby=${orderby}` : ''

  if (page <= 2 && maxPage <= 2) {
    return null
  }

  return (
    <ul className="flex gap-1">
      {page > 1 ? (
        <li>
          <Button rel="prev" linkUrl={`${route}${pathname}/${Math.max(page - 1, 1)}/${query}`}>
            ←
          </Button>
        </li>
      ) : null}
      <Pages page={page} maxPage={maxPage} query={query} route={route} pathname={pathname} />
      {page < maxPage ? (
        <li>
          <Button rel="next" linkUrl={`${route}${pathname}/${page + 1}/${query}`}>
            →
          </Button>
        </li>
      ) : null}
    </ul>
  )
}

interface ButtonProps extends React.HTMLProps<HTMLButtonElement> {
  children: React.ReactNode
  active?: boolean
  linkUrl: string
  rel?: 'prev' | 'next'
}

function Button({ children, active = false, linkUrl, rel }: ButtonProps) {
  const locale = useLocale()
  const className = tw(
    'bg-cf-black/[0.024] hover:bg-cf-black/5 text-12 px-3 py-1',
    active && 'text-cf-white bg-[#2c2d33] font-bold hover:bg-[#2c2d33]',
  )

  if (active) {
    return (
      <span className={className} aria-current="page">
        {children}
      </span>
    )
  }

  return (
    <Link className={className} href={addOrReplaceLocaleInUrl(linkUrl, locale)} rel={rel}>
      {children}
    </Link>
  )
}

interface PageItemProps {
  i: number
  page: number
  query: string
  route: string
  pathname: string
}

function PageItem({ i, page, query, route, pathname }: PageItemProps) {
  return (
    <li key={i}>
      <Button active={i === page} linkUrl={`${route}${pathname}/${i}/${query}`}>
        {i}
      </Button>
    </li>
  )
}

interface EllipsisProps {
  page: number
  query: string
  rel: 'prev' | 'next'
  route: string
  pathname: string
}

function Ellipsis({ page, query, rel, route, pathname }: EllipsisProps) {
  return (
    <li key={`ellipsis-${page}`}>
      <Button rel={rel} linkUrl={`${route}${pathname}/${page}/${query}`}>
        ...
      </Button>
    </li>
  )
}

interface PagesProps extends PaginationProps {
  query: string
}

function Pages({ page, maxPage, query, route, pathname }: PagesProps) {
  const numbers = getPageNumbers(page, maxPage)
  return numbers.map((item, index) =>
    // oxlint-disable-next-line no-nested-ternary
    item === '<' ? (
      <Ellipsis
        rel="prev"
        key="<"
        page={index + 1}
        query={query}
        route={route}
        pathname={pathname}
      />
    ) : item === '>' ? (
      <Ellipsis
        rel="next"
        key=">"
        page={+numbers[index - 1] + 1}
        query={query}
        route={route}
        pathname={pathname}
      />
    ) : (
      <PageItem key={item} i={item} page={page} query={query} route={route} pathname={pathname} />
    ),
  )
}

const getPageNumbers = (page: number, maxPage: number): (number | '<' | '>')[] => {
  if (maxPage <= 8) {
    return Array.from({ length: maxPage }, (_, i) => i + 1)
  }

  if (page <= 7) {
    return [...Array.from({ length: 8 }, (_, i) => i + 1), '>']
  }

  if (page + 5 <= maxPage) {
    return [1, 2, 3, '<', page - 2, page - 1, page, page + 1, page + 2, '>']
  }

  return [1, 2, 3, '<', ...Array.from({ length: 5 }, (_, i) => maxPage - 4 + i)]
}
